import { ComponentProps, forwardRef } from "react"

import { cn } from "@/lib/utils"

export interface BaseSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: React.ReactNode
}

export const Root = forwardRef(function Root(
  { className, children, ...props }: BaseSectionProps,
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-md border border-colors-border-secondary bg-white",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
})

interface HeaderSectionProps {
  bg?: "white" | "haze"
  hideBorder?: boolean
}

export const Header = forwardRef(function Header(
  {
    className,
    children,
    bg = "white",
    hideBorder = false,
    ...props
  }: BaseSectionProps & HeaderSectionProps,
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <div
      ref={ref}
      className={cn(
        "@container/section-header grid grid-cols-[1fr_auto] grid-rows-[auto_auto] items-start justify-between rounded-t-[inherit] px-5 py-6",
        {
          "bg-haze-100": bg === "haze",
          "bg-white": bg === "white",
        },
        { "border-b border-colors-border-secondary": !hideBorder },
        { "pb-4": hideBorder },
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
})

export const Title = forwardRef(function Title(
  { className, children, ...props }: ComponentProps<"span">,
  ref: React.Ref<HTMLHeadingElement>,
) {
  return (
    <span
      ref={ref}
      className={cn("row-start-1 text-h2 text-colors-text-primary", className)}
      {...props}
    >
      {children}
    </span>
  )
})

export const Subtitle = forwardRef(function Subtitle(
  { className, children, ...props }: ComponentProps<"span">,
  ref: React.Ref<HTMLHeadingElement>,
) {
  return (
    <span
      ref={ref}
      className={cn("row-start-2 text-h4 text-colors-text-primary", className)}
      {...props}
    >
      {children}
    </span>
  )
})

export const Body = forwardRef(function Body(
  { className, children, ...props }: BaseSectionProps,
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <div ref={ref} className={cn("p-5", className)} {...props}>
      {children}
    </div>
  )
})

export const Actions = forwardRef(function Body(
  { className, children, ...props }: BaseSectionProps,
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <div
      ref={ref}
      className={cn(
        "col-start-1 flex flex-grow flex-wrap justify-end gap-2 pt-2 @md/section-header:col-start-2 @md/section-header:row-span-2 @md/section-header:ml-5 @md/section-header:pt-0",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
})
